<template>
  <div class="pending-container">
    <div class="pending-content">
      <!-- Show error state if present -->
      <div v-if="projectStore.error" class="error-state">
        <div class="error-icon">
          <QIcon :path="mdiAlertCircle" :size="48" color="var(--color-error)" />
        </div>
        <Heading :level="1">Connection Error</Heading>
        <p class="error-message">{{ projectStore.error }}</p>
        <Button @click="retryConnection">Retry</Button>
      </div>

      <!-- Normal pending state -->
      <div v-else>
        <div class="pending-icon">
          <QIcon :path="mdiClockOutline" :size="48" color="var(--color-text-secondary)" />
        </div>
        <Heading :level="1">Your Edit is in the Queue</Heading>
        <p class="pending-message">
          Processing will begin shortly. No need to refresh—we'll start automatically when ready.
        </p>
        <div class="pending-spinner">
          <div class="spinner"></div>
          <span class="spinner-text">Waiting to start...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button, Heading } from '@dabble/quillui';
import { mdiAlertCircle, mdiClockOutline } from '@mdi/js';
import { useProjectStore } from '../stores/project';
import QIcon from './QIcon.vue';

const projectStore = useProjectStore();

function retryConnection() {
  // Clear error and reload
  projectStore.error = null;
  projectStore.load(projectStore.projectId);
}
</script>

<style scoped>
.pending-container {
  max-width: 600px;
  margin: 0 auto;
  padding: var(--spacing-xl);
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pending-content {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-lg);
}

.pending-icon {
  opacity: 0.6;
}

.pending-content h1 {
  margin: 0;
  color: var(--color-text-primary);
}

.pending-message {
  color: var(--color-text-secondary);
  font-size: 16px;
  line-height: 1.5;
  margin: 0;
}

.pending-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--color-bg-tertiary);
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.spinner-text {
  font-size: 14px;
  color: var(--color-text-secondary);
  font-weight: 500;
}

.error-state {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-lg);
}

.error-icon {
  opacity: 0.8;
}

.error-message {
  color: var(--color-text-primary);
  font-size: 16px;
  line-height: 1.5;
  margin: 0;
}
</style>
